/**
 * Lightweight event tracker + anonymous identity management
 * Tracks: page_view, product_view, search, wishlist_add, add_to_cart, checkout_started, order_placed
 */
import { BACKEND_URL } from './api';

const ANON_KEY = 'nc_anon_id';
const LEAD_KEY = 'nc_lead_id';

function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getAnonId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = `anon_${uuid()}`;
    localStorage.setItem(ANON_KEY, id);
    document.cookie = `anon_id=${id};path=/;max-age=${365 * 86400};SameSite=Lax`;
  }
  return id;
}

export function getLeadId() {
  return localStorage.getItem(LEAD_KEY) || getCookie('lead_id') || null;
}

export function setLeadId(id) {
  localStorage.setItem(LEAD_KEY, id);
  document.cookie = `lead_id=${id};path=/;max-age=${365 * 86400};SameSite=Lax`;
}

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? m[2] : null;
}

// Debounce rapid duplicate events
let lastEvent = '';
let lastEventTime = 0;

// Persist the FIRST-TOUCH referrer + UTM for this browser session so that
// every subsequent pageview shares the same attribution. Without this,
// a visitor who lands on `/` from Google and then clicks to `/products`
// would count the /products hit as "direct" (internal navigation) and we'd
// lose the Google Organic source. Reset when the browser closes (sessionStorage).
const ATTRIBUTION_KEY = 'nc_attribution';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function captureAttribution() {
  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (existing) return JSON.parse(existing);
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_PARAMS.forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 200);
    });
    const data = {
      referrer: document.referrer || '',
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
      ...utm,
    };
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
    return data;
  } catch {
    return {};
  }
}

export async function track(eventType, meta = {}) {
  const key = `${eventType}:${JSON.stringify(meta)}`;
  const now = Date.now();
  if (key === lastEvent && now - lastEventTime < 2000) return;
  lastEvent = key;
  lastEventTime = now;

  const anonId = getAnonId();
  const leadId = getLeadId();
  const attribution = captureAttribution();

  try {
    await fetch(`${BACKEND_URL}/api/events/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: eventType,
        meta: {
          ...meta,
          page_url: window.location.pathname,
          referrer: attribution.referrer || '',
          utm_source: attribution.utm_source || null,
          utm_medium: attribution.utm_medium || null,
          utm_campaign: attribution.utm_campaign || null,
          utm_term: attribution.utm_term || null,
          utm_content: attribution.utm_content || null,
          landing_path: attribution.landing_path || '',
        },
        anon_id: anonId,
        lead_id: leadId,
      }),
    });
  } catch (e) {
    // Silent fail - tracking should never block UX
  }
}

// Identity claim after login/signup
export async function claimIdentity(customerId, phone, email, provider = 'sms') {
  try {
    const anonId = getAnonId();
    const leadId = getLeadId();

    await fetch(`${BACKEND_URL}/api/identity/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        customer_id: customerId,
        phone: phone || null,
        email: email || null,
        provider,
        verified: true,
        anon_id: anonId,
        lead_id: leadId,
      }),
    });
  } catch (e) {
    console.warn('Identity claim failed:', e);
  }
}

// Initialize anon_id on import
getAnonId();
